import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { PropertyProvider } from './context/PropertyContext';
import { AuthProvider } from './context/AuthContext';
import { SiteContentProvider } from './context/SiteContentContext';
import { useProperty } from './context/PropertyContext';
import { useAuth } from './context/AuthContext';
import usePushNotifications from './hooks/usePushNotifications';
import './App.css';

// When running as native app (Android/iOS), point API calls to the live server
if (Capacitor.isNativePlatform()) {
  axios.defaults.baseURL = process.env.REACT_APP_SERVER_URL || 'https://www.spaczo.com';
}

// Eager — needed on first paint
import Navbar from './components/Navbar/Navbar';
import FinderPage from './components/Finder/FinderPage';
import AuthModal from './components/Auth/AuthModal';
import SplashScreen from './components/Splash/SplashScreen';
import PayRentPage from './components/PayRent/PayRentPage';
import RateStayPage from './components/TenantPortal/RateStayPage';
import AgreementPage from './components/TenantPortal/AgreementPage';
import AnnouncementBar from './components/Announcement/AnnouncementBar';
import { saveJoinRef } from './lib/onboardingRef';

// Lazy — only loaded when user navigates to that tab
const ListingPage      = React.lazy(() => import('./components/Listing/ListingPage'));
const FavoritesPage    = React.lazy(() => import('./components/Favorites/FavoritesPage'));
const MyListingsPage   = React.lazy(() => import('./components/MyListings/MyListingsPage'));
const RadarView        = React.lazy(() => import('./components/Radar/RadarView'));
const PropertyDetail   = React.lazy(() => import('./components/PropertyDetail/PropertyDetail'));
const SupportPage      = React.lazy(() => import('./components/Support/SupportPage'));
const SavedAlertsPage  = React.lazy(() => import('./components/Alerts/SavedAlertsPage'));
const RentAgreementPage = React.lazy(() => import('./components/RentAgreement/RentAgreementPage'));
const BlogPage         = React.lazy(() => import('./components/Blog/BlogPage'));
const VerificationPage = React.lazy(() => import('./components/Verification/VerificationPage'));
const AdminPanel       = React.lazy(() => import('./components/Admin/AdminPanel'));
const PropertyManagementPage = React.lazy(() => import('./components/PropertyManagement/PropertyManagementPage'));
const JoinPage         = React.lazy(() => import('./components/Join/JoinPage'));
const TeamInvite       = React.lazy(() => import('./components/Owner/TeamInvite'));
const RentReceiptGenerator = React.lazy(() => import('./components/Tools/RentReceiptGenerator'));
const ReceiptsPage     = React.lazy(() => import('./components/TenantPortal/ReceiptsPage'));
const INVITE_KEY = 'spaczo_team_invite';

const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
    <div style={{ width: 32, height: 32, border: '3px solid #f3f3f3', borderTop: '3px solid #E53935', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 16, padding: 24, textAlign: 'center' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>Something went wrong</h2>
          <p style={{ color: '#888', maxWidth: 360 }}>An unexpected error occurred. Please refresh the page to continue.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', borderRadius: 8, background: '#6c63ff', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const { activeTab, setActiveTab, selectedProperty, setSelectedProperty, finderCategory, setFinderCategory, viewPickerCategory, setViewPickerCategory } = useProperty();
  const [showAuth, setShowAuth] = useState(false);
  // Team invite link (/?team_invite=<token>). Kept for the session so it
  // survives signing in with Google (which leaves the site and comes back).
  const [teamInvite, setTeamInvite] = useState(() => {
    try { return sessionStorage.getItem(INVITE_KEY); } catch { return null; }
  });

  // Deep link: open property detail if ?property=<id>, or tab if ?tab=<name>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('team_invite');
    if (invite && /^[a-f0-9]{48}$/.test(invite)) {
      try { sessionStorage.setItem(INVITE_KEY, invite); } catch {}
      setTeamInvite(invite);
      window.history.replaceState({}, '', '/');
      return;
    }
    // /p/<id> is the shareable listing link; the server normally redirects it
    // to ?property=<id>, but handle it here too (dev server, native app).
    const shortLink = window.location.pathname.match(/^\/p\/([0-9a-f-]{36})\/?$/i);
    const pid = params.get('property') || shortLink?.[1];
    const tab = params.get('tab');
    const join = params.get('join');
    if (join) {
      window.history.replaceState({}, '', '/');
      if (saveJoinRef(join)) setActiveTab('join');
      else setActiveTab('list');
    } else if (pid) {
      window.history.replaceState({}, '', shortLink ? '/' : window.location.pathname);
      axios.get(`/api/properties/${pid}`)
        .then(res => { if (res.data.success) setSelectedProperty(res.data.data); })
        .catch(() => {});
    } else if (tab) {
      const section = params.get('section');
      if (tab === 'mylistings' && /^(listings|leads|rent|team|plan)$/.test(section || '')) {
        try { sessionStorage.setItem('spaczo_owner_section', section); } catch {}
      }
      window.history.replaceState({}, '', window.location.pathname);
      setActiveTab(tab);
    }
  }, []); // eslint-disable-line

  // Clear selected property when switching tabs
  useEffect(() => {
    setSelectedProperty(null);
  }, [activeTab]); // eslint-disable-line

  // ── Back-button navigation ──────────────────────────────────────
  // Seed history stack on mount
  useEffect(() => {
    window.history.replaceState({ spaczo: 'base' }, '');
    window.history.pushState({ spaczo: 'active' }, '');
  }, []); // eslint-disable-line

  // Push a history entry on every navigation so back always has somewhere to go
  useEffect(() => {
    window.history.pushState({ spaczo: 'active' }, '');
  }, [activeTab, selectedProperty, finderCategory, viewPickerCategory]);

  useEffect(() => {
    const onPop = () => {
      // Back on the full-screen map only closes the map (FinderPage does it).
      if (window.__spaczoMapFullscreen) return;
      if (selectedProperty) {
        setSelectedProperty(null);
      } else if (viewPickerCategory) {
        setViewPickerCategory(null);
      } else if (finderCategory) {
        setFinderCategory(null);
      } else if (activeTab !== 'finder') {
        setActiveTab('finder');
      } else {
        return; // already at root — let browser handle
      }
      window.history.pushState({ spaczo: 'active' }, '');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [selectedProperty, viewPickerCategory, finderCategory, activeTab,
      setSelectedProperty, setViewPickerCategory, setFinderCategory, setActiveTab]);

  // Analytics: count one visit per browser session (a daily total — nothing
  // about the visitor is stored).
  useEffect(() => {
    if (isBot) return;
    try {
      if (sessionStorage.getItem('spaczo_visit_counted')) return;
      sessionStorage.setItem('spaczo_visit_counted', '1');
    } catch { return; }
    axios.post('/api/content/visit').catch(() => {});
  }, []);

  const { user, recoveryMode } = useAuth();
  const [authInitialMode, setAuthInitialMode] = useState('login');

  // Came back from Google sign-in started on the owner welcome page → go to
  // the listing form instead of the home page.
  useEffect(() => {
    if (!user) return;
    try {
      const next = sessionStorage.getItem('spaczo_after_login_tab');
      if (next === 'list') { sessionStorage.removeItem('spaczo_after_login_tab'); setActiveTab('list'); }
    } catch {}
  }, [user]); // eslint-disable-line

  // Push notifications — ask after 8s, works for both logged-in and anonymous users
  const userType = activeTab === 'list' || activeTab === 'mylistings' ? 'lister' : 'seeker';
  usePushNotifications({ user, userType, city: null });

  // Open reset modal when recovery link is clicked
  useEffect(() => {
    if (recoveryMode) {
      setAuthInitialMode('reset');
      setShowAuth(true);
    }
  }, [recoveryMode]);

  return (
    <div className="app">
      <AnnouncementBar />
      <Navbar />
      <main className="app-main">
        {activeTab === 'finder' && <FinderPage />}
        {activeTab !== 'finder' && (
          <div className="page-scroll" key={activeTab}>
            <Suspense fallback={<PageLoader />}>
              {activeTab === 'list'         && <ListingPage />}
              {activeTab === 'favorites'    && <FavoritesPage />}
              {activeTab === 'mylistings'   && <MyListingsPage />}
              {activeTab === 'radar'        && <RadarView />}
              {activeTab === 'support'      && <SupportPage />}
              {activeTab === 'alerts'       && <SavedAlertsPage />}
              {activeTab === 'agreement'    && <RentAgreementPage />}
              {activeTab === 'join'         && <JoinPage />}
              {activeTab === 'blog'         && <BlogPage />}
              {activeTab === 'verification' && <VerificationPage />}
              {activeTab === 'admin'        && <AdminPanel />}
              {activeTab === 'receipt'      && <RentReceiptGenerator />}
            </Suspense>
          </div>
        )}
      </main>
      {selectedProperty && (
        <Suspense fallback={null}>
          <div className="property-detail-slide">
            <PropertyDetail
              key={selectedProperty._id}
              property={selectedProperty}
              onClose={() => setSelectedProperty(null)}
            />
          </div>
        </Suspense>
      )}
      {showAuth && <AuthModal initialMode={authInitialMode} onClose={() => { setShowAuth(false); setAuthInitialMode('login'); }} />}
      {teamInvite && (
        <Suspense fallback={null}>
          <TeamInvite token={teamInvite} onClose={() => setTeamInvite(null)} />
        </Suspense>
      )}
    </div>
  );
}

// Skip splash for bots, crawlers and PageSpeed — they never need the animation
const isBot = /bot|crawler|spider|lighthouse|chrome-lighthouse|pagespeed|googlebot|prerender/i.test(navigator.userAgent);

// /pay-rent/:id is a standalone public page (tenants open it with no account,
// usually from a WhatsApp link) — it renders outside the whole app shell,
// with no Navbar/Auth/Property context needed.
const payRentMatch  = window.location.pathname.match(/^\/pay-rent\/([a-zA-Z0-9-]+)\/?$/);
// /rate-stay/:id and /agreement/:id are the same kind of standalone public
// page as /pay-rent/:id — a tenant opens them from a link the owner shares,
// no account needed, no Navbar/Auth/Property context.
const rateStayMatch  = window.location.pathname.match(/^\/rate-stay\/([a-zA-Z0-9-]+)\/?$/);
const agreementMatch = window.location.pathname.match(/^\/agreement\/([a-zA-Z0-9-]+)\/?$/);
const receiptsMatch  = window.location.pathname.match(/^\/rent-receipts\/([a-zA-Z0-9-]+)\/?$/);

export default function App() {
  // The intro plays once per visit, and never when someone opens a shared
  // link (listing, onboarding, a specific tab): they came for that page.
  const [showSplash, setShowSplash] = useState(() =>
    !isBot && !sessionStorage.getItem('spaczo_splash_seen') &&
    !/[?&](property|join|tab)=/.test(window.location.search) && !/^\/p\//.test(window.location.pathname)
  );

  if (payRentMatch)  return <PayRentPage rentId={payRentMatch[1]} />;
  if (rateStayMatch)  return <RateStayPage tenantId={rateStayMatch[1]} />;
  if (agreementMatch) return <AgreementPage tenantId={agreementMatch[1]} />;
  if (receiptsMatch)  return <Suspense fallback={null}><ReceiptsPage tenantId={receiptsMatch[1]} /></Suspense>;

  return (
    <AuthProvider>
      <PropertyProvider>
        <SiteContentProvider>
          <ErrorBoundary>
            {showSplash && <SplashScreen onDone={() => { sessionStorage.setItem('spaczo_splash_seen', '1'); setShowSplash(false); }} />}
            <AppContent />
          </ErrorBoundary>
        </SiteContentProvider>
      </PropertyProvider>
    </AuthProvider>
  );
}
